import { ApiEntity, ComponentEntity, RELATION_OWNED_BY, stringifyEntityRef, SystemEntity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { EntityLayout } from '@backstage/plugin-catalog';
import { catalogApiRef, getEntityRelations, useEntity } from '@backstage/plugin-catalog-react';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { GCPOverviewComponent } from '@internal/backstage-plugin-gcp';
import { ResourceType } from '@internal/backstage-plugin-gcp-common';
import { MessagingResource } from '@internal/open-catalog';
import { EntityReleaseContent, isReleaseManagerAvailable } from '@internal/backstage-plugin-release-manager';
import { EntityTranslationsContent, isI18nFeatureEnabled } from '@internal/backstage-plugin-i18n-dictionary-manager';
import { Grid } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import useAsync from 'react-use/esm/useAsync';
import { EntityComponentsCard, EntityContentWrapper, EntityInformationCard, EntityLinkCard, EntityTeamCard } from '../../../components/catalog';
import { SystemMessagingPage } from './messaging';
import { SystemFeatureFlagPage } from './featureflag';
import { SystemSchedulerPage } from './scheduler';
import { SystemApiPage } from './api';
import { EmptyState } from '@backstage/core-components';

const OverviewContent = (props: { system: SystemEntity, components: ComponentEntity[] }) => {

  const { system, components } = props;
  const systemEntityRef = stringifyEntityRef(system);

  return (<EntityContentWrapper>
    <Grid item md={8} xs={12}>
      <EntityInformationCard />
    </Grid>
    <Grid item md={4} xs={12}>
      <EntityLinkCard entity={system} />
    </Grid>
    <Grid item xs={12}>
      <EntityComponentsCard components={components} owner={systemEntityRef}/>
    </Grid>
  </EntityContentWrapper>);
}

const MessagingContent = (props: { system: SystemEntity, components: ComponentEntity[] }) => {

  const { system, components } = props;

  const catalogApi = useApi(catalogApiRef);

  const [resources, setResources] = React.useState<MessagingResource[]>();

  const loadResources = useCallback(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        kind: 'Resource',
        ['relations.partof']: [stringifyEntityRef(system)],
        ['spec.type']: 'Messaging'
      }
    });
    setResources(response.items as MessagingResource[]);
    return response.items as MessagingResource[];
  }, [catalogApi, system]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  return (<EntityContentWrapper>
    <Grid item xs={12}>
      {resources && <SystemMessagingPage system={system} components={components} resources={resources} resourcesReload={loadResources} />}
    </Grid>
  </EntityContentWrapper>);
}

const OwnerTeamContent = (props: { system: SystemEntity }) => {

  const { system } = props;
  const ownedBy = getEntityRelations(system, RELATION_OWNED_BY)[0];

  return (<EntityContentWrapper>
    <Grid item xs={12}>
      <EntityTeamCard entityRef={ownedBy} showRefreshButton />
    </Grid>
  </EntityContentWrapper>);
}

const FeaturesContent = (props: { system: SystemEntity}) => {
  const { system } = props;

  return (<EntityContentWrapper>
    <Grid item xs={12}>
      <SystemFeatureFlagPage system={system} />
    </Grid>
  </EntityContentWrapper>);
}

const SchedulerContent = () => {

  return (<EntityContentWrapper>
    <Grid item xs={12}>
      <SystemSchedulerPage />
    </Grid>
  </EntityContentWrapper>);
}

const ApiContent = (props: { components: ComponentEntity[] }) => {

  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const { components, } = props;
  const services = components.filter(component => component.spec.type === 'service')

  const { value: apiDocs } = useAsync(async () => {
    
    const ownerGroup = entity.relations?.find(relation => relation.type === 'ownedBy');
    if(!ownerGroup)
      return [] as ApiEntity[];

    const response = await catalogApi.getEntities({
      filter: { kind: 'API', ['relations.ownedby']: [ownerGroup.targetRef],
        ['spec.type']: ['openapi', 'asyncapi'],
        ['relations.apiProvidedBy']: services.map(service => stringifyEntityRef({ kind: 'Component', name: service.metadata.name, namespace: entity.metadata.namespace }))
       }
    });


    return response.items as ApiEntity[];
  }, [catalogApi]);

  if (!services || services.length === 0 || !apiDocs || apiDocs.length === 0) {
    return (<EmptyState missing="content" title="The system has no API docs." />);
  }

  return (<EntityContentWrapper>
    <Grid item xs={12}>
      <>{apiDocs && <SystemApiPage services={services} apiDocs={apiDocs} />}</>
    </Grid>
  </EntityContentWrapper>);
}

export const SystemPage = () => {

  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const messagingDedicatedCluster = entity?.metadata?.annotations?.['open.totvs.io/messaging-dedicated-cluster'];

  const { value: components } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind: 'Component', ['relations.partof']: [stringifyEntityRef(entity)] }
    });
    return response.items as ComponentEntity[];
  }, [catalogApi]);

  const extraContextMenuItems = [
    {
      title: 'Requests',
      Icon: AssignmentIcon,
      onClick: () => navigate('/requests', {
        state: {
          system: stringifyEntityRef({
            kind: 'system',
            namespace: entity.metadata.namespace || '',
            name: entity.metadata.name || '',
          }),
        },
      }),
    },
  ];

  return (<EntityLayout UNSTABLE_contextMenuOptions={{ disableUnregister: 'hidden' }} UNSTABLE_extraContextMenuItems={extraContextMenuItems}>
    <EntityLayout.Route path="/" title="Overview">
      <>{components && <OverviewContent system={entity as SystemEntity} components={components} />}</>
    </EntityLayout.Route>
    { messagingDedicatedCluster === 'true' && (
    <EntityLayout.Route path="/messaging" title="Messaging">
      <>{components && <MessagingContent system={entity as SystemEntity} components={components} />}</>
    </EntityLayout.Route>)}
    <EntityLayout.Route path="/team" title="Team">
      <OwnerTeamContent system={entity as SystemEntity} />
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      <EntityTechdocsContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/apis" title="Apis">
      <>{components && <ApiContent components={components} />}</>
    </EntityLayout.Route>

      <EntityLayout.Route path="/features" title="Features">
        <>{components && <FeaturesContent system={entity as SystemEntity} />}</>
      </EntityLayout.Route>

    <EntityLayout.Route path="/databases" title="Databases">
      <GCPOverviewComponent gcpComponentType={ResourceType.GCP_SQL_INSTANCE} />
    </EntityLayout.Route>
    <EntityLayout.Route path="/scheduler" title="Scheduler">
      <SchedulerContent />
    </EntityLayout.Route>
    {isReleaseManagerAvailable(entity) && (
      <EntityLayout.Route path="/releases" title="Releases">
        <EntityContentWrapper>
          <Grid item xs={12}>
            <EntityReleaseContent />
          </Grid>
        </EntityContentWrapper>
      </EntityLayout.Route>
    )}
    {isI18nFeatureEnabled() && (
      <EntityLayout.Route path="/translations" title="Translations">
      <EntityTranslationsContent />
    </EntityLayout.Route>
    )}
  </EntityLayout>);
}
